import React from 'react';
import { LockKeyhole, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isDemoHost } from '../../config/api.config';

export type LicenseType = 'club' | 'evaluation' | 'club-or-evaluation';

interface LicenseGateProps {
  licenseType: LicenseType;
  children: React.ReactNode;
  dashboardName: string;
}

const LICENSE_LABELS: Record<LicenseType, string> = {
  club: 'باشگاه مشتریان',
  evaluation: 'ارزیابی',
  'club-or-evaluation': 'باشگاه مشتریان یا ارزیابی',
};

const LICENSE_URLS: Record<LicenseType, string> = {
  club: 'https://smartx.ir/services/club',
  evaluation: 'https://smartx.ir/services/evaluation/',
  'club-or-evaluation': 'https://smartx.ir/services/',
};

const LicenseGate: React.FC<LicenseGateProps> = ({ licenseType, children, dashboardName }) => {
  const { licenses } = useAuth();

  if (isDemoHost()) {
    return <>{children}</>;
  }
  
  // Check if user has the required license
  let hasLicense = false;
  
  if (licenses) {
    if (licenseType === 'club-or-evaluation') {
      hasLicense = licenses.club || licenses.evaluation;
    } else {
      hasLicense = Boolean(licenses[licenseType]);
    }
  }
  
  if (hasLicense) {
    return <>{children}</>;
  }
  
  // Show locked state with beautiful UI
  // Use different color schemes based on license type
  const colorScheme: Record<string, any> = {
    club: {
      glow: 'from-cyan-400 to-blue-400',
      icon: 'from-cyan-400 to-blue-500',
      button: 'from-cyan-500 to-blue-600',
      accent: 'from-cyan-50 to-blue-50',
      border: 'border-cyan-200',
      sparkle: 'text-cyan-600'
    },
    evaluation: {
      glow: 'from-lime-400 to-green-400',
      icon: 'from-lime-400 to-green-500',
      button: 'from-lime-500 to-green-600',
      accent: 'from-lime-50 to-green-50',
      border: 'border-lime-200',
      sparkle: 'text-lime-600'
    },
    'club-or-evaluation': {
      glow: 'from-amber-400 to-orange-400',
      icon: 'from-amber-400 to-orange-500',
      button: 'from-amber-500 to-orange-600',
      accent: 'from-amber-50 to-orange-50',
      border: 'border-amber-200',
      sparkle: 'text-amber-600'
    }
  };

  const colors = colorScheme[licenseType];
  
  return (
    <div className="container-dashboard h-screen flex items-center justify-center p-4 overflow-hidden">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-4 text-center">
          <div className="relative mt-4 mb-4">
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.glow} rounded-full blur-xl opacity-20`}></div>
            <div className={`relative bg-gradient-to-br ${colors.icon} rounded-full w-20 h-20 mx-auto flex items-center justify-center shadow-lg`}>
              <LockKeyhole className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h2 className="mt-4 text-xl font-bold text-gray-800">
            {dashboardName} قفل است
          </h2>
          
          <p className="mt-2 text-xs leading-relaxed text-gray-600">
            برای دسترسی به این داشبورد، نیاز به لایسنس {LICENSE_LABELS[licenseType]} دارید.
          </p>
          
          <div className="mt-4 space-y-2">
            <div className={`bg-gradient-to-r ${colors.accent} rounded-lg p-2 border ${colors.border} mb-2`}>
              <Sparkles className={`w-4 h-4 ${colors.sparkle} mx-auto mb-1`} />
              <p className="text-xs text-gray-700">
                با فعالسازی این لایسنس، به امکانات پیشرفته و گزارش‌های تحلیلی دسترسی پیدا کنید
              </p>
            </div>
            
            <button 
              onClick={() => {
                window.open(LICENSE_URLS[licenseType], '_blank');
              }}
              className={`w-full bg-gradient-to-r ${colors.button} text-white rounded-lg py-2 px-4 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-xs`}
            >
              درخواست لایسنس
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseGate;
